import { createSelector } from '@reduxjs/toolkit';

import { CoursesListViewConsts } from 'Courses/pages/courses-list/CoursesListView.consts';

import { DEFAULT_TIMEZONE } from '@consts/timezone';

import type { UsersState } from './types';

import type { RootState } from 'Store';

const selectSelf = (state: RootState): UsersState => state.user;

export const selectCurrentUserInfo = createSelector(
  selectSelf,
  userState => userState.currentUserInfo
);

export const selectCurrentUserInfoStatus = createSelector(
  selectSelf,
  userState => userState.currentUserInfo
);

export const selectUserDisplayName = createSelector(selectCurrentUserInfo, currentUserInfo => {
  if (currentUserInfo.displayName) {
    return currentUserInfo.displayName;
  }

  const firstName = currentUserInfo.firstName || '';
  const lastName = currentUserInfo.lastName || '';

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  if (firstName) {
    return firstName;
  }

  if (lastName) {
    return lastName;
  }

  return null;
});

export const selectUserEmail = createSelector(selectCurrentUserInfo, userInfo => userInfo?.email);

export const selectUserAvatar = createSelector(selectCurrentUserInfo, userInfo => userInfo?.avatar);

export const selectUserAvatarColor = createSelector(
  selectCurrentUserInfo,
  userInfo => userInfo?.avatarColor
);

export const selectIsStartBannerHidden = createSelector(
  selectSelf,
  userState => userState.preferences.isStartBannerHidden ?? false
);

export const selectIsMachineBarPinned = createSelector(
  selectCurrentUserInfo,
  userInfo => userInfo?.isMachineBarPinned
);

export const selectAreTipsHidden = createSelector(
  selectCurrentUserInfo,
  userInfo => userInfo?.areTipsHidden
);

export const selectIsSaveCurrentUserInfoSuccess = createSelector(
  selectSelf,
  userState => userState.isSaveCurrentUserInfoSuccess ?? false
);

export const selectIsSaveCurrentUserInfoLoading = createSelector(
  selectSelf,
  userState => userState.isSaveCurrentUserInfoLoading ?? false
);

export const selectIsGetCurrentUserInfoLoading = createSelector(
  selectSelf,
  userState => userState.isGetCurrentUserInfoLoading ?? false
);

export const selectHasUserInfo = createSelector(
  selectCurrentUserInfo,
  userInfo => !!(userInfo && Object.keys(userInfo).length > 0)
);

export const selectUserInitials = createSelector(selectCurrentUserInfo, userInfo => {
  const firstName = userInfo?.firstName?.charAt(0) ?? '';
  const lastName = userInfo?.lastName?.charAt(0) ?? '';
  return `${firstName}${lastName}`.toUpperCase();
});

export const selectIsStartBannerVisible = createSelector(
  selectIsStartBannerHidden,
  isHidden => !isHidden
);

export const selectIsMachineBarUnpinned = createSelector(
  selectIsMachineBarPinned,
  isPinned => !isPinned
);

export const selectAreTipsVisible = createSelector(
  selectSelf,
  state => !state.currentUserInfo.areTipsHidden
);

export const selectIsUserInfoSaveSuccessful = createSelector(
  selectIsSaveCurrentUserInfoSuccess,
  isSuccess => isSuccess === true
);

export const selectShouldShowWelcomeMessage = createSelector(
  [selectHasUserInfo, selectIsStartBannerVisible],
  (hasUserInfo, isStartBannerVisible) => hasUserInfo && isStartBannerVisible
);

export const selectCanShowTips = createSelector(
  [selectHasUserInfo, selectAreTipsVisible],
  (hasUserInfo, areTipsVisible) => hasUserInfo && areTipsVisible
);

export const selectIsUserSettingsComplete = createSelector(
  selectCurrentUserInfo,
  userInfo =>
    !!(userInfo?.firstName && userInfo?.lastName && userInfo?.displayName && userInfo?.languageCode)
);

export const selectUserPreferences = createSelector(selectSelf, userState => userState.preferences);

export const selectUserTheme = createSelector(
  selectUserPreferences,
  preferences => preferences.theme
);

export const selectUserLanguage = createSelector(
  selectUserPreferences,
  preferences => preferences.language
);

export const selectUserTimezone = createSelector(
  selectUserPreferences,
  preferences => preferences.timezone || DEFAULT_TIMEZONE
);

export const selectIsUserDarkTheme = createSelector(selectUserTheme, theme => theme === 'dark');

export const selectIsUserLightTheme = createSelector(selectUserTheme, theme => theme === 'light');

export const selectCoursePreferences = createSelector(
  selectUserPreferences,
  preferences =>
    preferences?.coursePreferences ?? {
      viewMode: CoursesListViewConsts.defaultViewMode
    }
);

export const selectCourseViewMode = createSelector(
  selectCoursePreferences,
  coursePreferences => coursePreferences?.viewMode ?? CoursesListViewConsts.defaultViewMode
);
